/**
 * Client V3 reusable components.
 *
 * Kept in a single file to minimise import sprawl while still giving each
 * page small, well-named building blocks. All consume CSS variables from
 * `client-v3.css` (loaded globally from index.js).
 */
import { Link, NavLink, useNavigate, useParams } from "react-router-dom";
import {
    Home as HomeIcon,
    Gamepad2,
    Wallet as WalletIcon,
    Inbox as InboxIcon,
    UserRound,
    ChevronLeft,
    ChevronRight,
    Bell,
    Sparkles,
    Spade,
    Diamond,
    Disc,
    Coins,
    Fish,
    Dice5,
} from "lucide-react";

// ---------- Page wrapper ----------
export function ClientV3Page({ children, pad = true }) {
    return (
        <div className={`v3-shell${pad ? " px-4 pt-4" : ""}`}>{children}</div>
    );
}

// ---------- Header (top bar with optional back arrow) ----------
export function ClientV3Header({ title, back = false, right = null }) {
    const navigate = useNavigate();
    return (
        <div
            className="flex items-center justify-between mb-4"
            data-testid="v3-header"
        >
            <div className="flex items-center gap-2">
                {back && (
                    <button
                        onClick={() => navigate(-1)}
                        className="p-2 -ml-2 rounded-full hover:bg-black/5"
                        data-testid="v3-back-btn"
                        aria-label="Back"
                    >
                        <ChevronLeft className="w-5 h-5" />
                    </button>
                )}
                <h1 className="text-lg font-bold">{title}</h1>
            </div>
            <div className="flex items-center gap-1">{right}</div>
        </div>
    );
}

// ---------- Card ----------
export function ClientV3Card({ children, className = "", ...rest }) {
    return (
        <div className={`v3-card p-4 ${className}`} {...rest}>
            {children}
        </div>
    );
}

// ---------- Vendor header (the big white card on top of HomeV3 / ProfileV3) ----------
export function ClientV3VendorHeader({ vendor, client, onAccount, compact = false }) {
    const initial = (
        client?.username || vendor?.name || "?"
    )[0]?.toUpperCase();
    return (
        <div
            className={`v3-card flex items-center gap-3 ${compact ? "p-3" : "p-4"}`}
            data-testid="v3-vendor-header"
        >
            <div
                className="v3-vendor-avatar flex items-center justify-center text-2xl font-bold"
                style={{ background: "linear-gradient(135deg,#fef3c7,#fde68a)", color: "#92400e" }}
            >
                {initial}
            </div>
            <div className="flex-1 min-w-0">
                <div className="font-bold text-base truncate">
                    {client?.username || "Guest"}
                </div>
                <div className="text-xs" style={{ color: "var(--v3-muted)" }}>
                    {vendor?.name}
                    {vendor?.id ? ` · ID ${vendor.id.slice(0, 8)}` : ""}
                </div>
            </div>
            {onAccount && (
                <button
                    onClick={onAccount}
                    className="v3-btn v3-btn-primary"
                    style={{ padding: "8px 14px", fontSize: 13 }}
                    data-testid="v3-vendor-header-account-btn"
                >
                    My Account
                    <ChevronRight className="w-4 h-4" />
                </button>
            )}
        </div>
    );
}

// ---------- Stat pill row ----------
export function ClientV3StatRow({ stats }) {
    return (
        <div className="grid grid-cols-3 gap-2" data-testid="v3-stat-row">
            {stats.map((s, i) => (
                <div
                    key={i}
                    className="v3-card text-center py-3"
                    data-testid={`v3-stat-${s.label.toLowerCase().replace(/\s+/g, "-")}`}
                >
                    <div className="text-xl font-bold">{s.value}</div>
                    <div
                        className="text-[11px] mt-0.5"
                        style={{ color: "var(--v3-muted)" }}
                    >
                        {s.label}
                    </div>
                </div>
            ))}
        </div>
    );
}

// ---------- Banner ----------
export function ClientV3Banner({ title, subtitle, onClick }) {
    return (
        <button
            onClick={onClick}
            className="v3-banner w-full text-left"
            data-testid="v3-banner"
        >
            <Sparkles className="w-6 h-6 flex-shrink-0" />
            <div className="flex-1 min-w-0">
                <div className="font-bold text-sm">{title}</div>
                {subtitle && (
                    <div className="text-xs opacity-90 truncate">{subtitle}</div>
                )}
            </div>
            <ChevronRight className="w-5 h-5 opacity-90" />
        </button>
    );
}

// ---------- Section title with optional "see all" ----------
export function ClientV3SectionTitle({ title, link, linkLabel = "See all" }) {
    return (
        <div className="v3-section-title">
            <span>{title}</span>
            {link && (
                <Link to={link} className="v3-section-title-link">
                    {linkLabel}
                </Link>
            )}
        </div>
    );
}

// ---------- List row ----------
export function ClientV3ListItem({ icon: Icon, label, right, onClick, testId }) {
    return (
        <button
            onClick={onClick}
            className="v3-list-row"
            data-testid={testId}
        >
            <span className="v3-list-row-icon">
                {Icon ? <Icon className="w-4 h-4" /> : null}
            </span>
            <span className="flex-1 font-semibold">{label}</span>
            {right && (
                <span
                    className="text-sm"
                    style={{ color: "var(--v3-muted)" }}
                >
                    {right}
                </span>
            )}
            <ChevronRight
                className="w-4 h-4"
                style={{ color: "var(--v3-muted)" }}
            />
        </button>
    );
}

// ---------- Game card ----------
const GAME_ICON_MAP = {
    Spade,
    Diamond,
    Disc,
    Coins,
    Fish,
};

export function ClientV3GameCard({ game, onClick }) {
    const Icon = GAME_ICON_MAP[game?.icon] || Dice5;
    return (
        <button
            onClick={onClick}
            className="v3-game-card text-left"
            data-testid={`v3-game-card-${game?.code}`}
        >
            <div className="v3-game-card-art">
                <Icon />
            </div>
            <div className="p-3">
                <div className="font-bold text-sm truncate">{game?.name}</div>
                <div
                    className="text-[11px] mt-0.5 truncate"
                    style={{ color: "var(--v3-muted)" }}
                >
                    {game?.description || "Tap to play"}
                </div>
            </div>
        </button>
    );
}

// ---------- Bottom nav ----------
export const V3_NAV_ITEMS = [
    { key: "home", label: "Home", icon: HomeIcon, to: "" },
    { key: "games", label: "Games", icon: Gamepad2, to: "games" },
    { key: "wallet", label: "Wallet", icon: WalletIcon, to: "wallet" },
    { key: "inbox", label: "Inbox", icon: InboxIcon, to: "inbox" },
    { key: "me", label: "Me", icon: UserRound, to: "me" },
];

export function ClientV3BottomNav() {
    const { vendorSlug } = useParams();
    const base = `/v/${vendorSlug}`;
    return (
        <nav
            className="v3-bottom-nav"
            style={{ gridTemplateColumns: `repeat(${V3_NAV_ITEMS.length}, 1fr)` }}
            data-testid="v3-bottom-nav"
        >
            {V3_NAV_ITEMS.map((item) => {
                const Icon = item.icon;
                const path = item.to ? `${base}/${item.to}` : base;
                return (
                    <NavLink
                        key={item.key}
                        to={path}
                        end={item.to === ""}
                        className="v3-bottom-nav-item"
                        data-testid={`v3-nav-${item.key}`}
                    >
                        {({ isActive }) => (
                            <span
                                style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 2 }}
                                data-active={isActive ? "true" : "false"}
                            >
                                <Icon className="w-5 h-5" />
                                <span>{item.label}</span>
                            </span>
                        )}
                    </NavLink>
                );
            })}
        </nav>
    );
}

// ---------- Notification bell ----------
export function ClientV3Bell({ onClick, count = 0 }) {
    return (
        <button
            onClick={onClick}
            className="relative p-2 rounded-full hover:bg-black/5"
            data-testid="v3-bell"
            aria-label="Inbox"
        >
            <Bell className="w-5 h-5" />
            {count > 0 && (
                <span
                    className="absolute top-1 right-1 w-2 h-2 rounded-full"
                    style={{ background: "var(--v3-danger)" }}
                />
            )}
        </button>
    );
}
